import Link from "@mui/material/Link";
import { isFetchBaseQueryError } from "_shared/_errors/api-error-message";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import type { AppDispatch } from "_shared/_stores/store-types";
import downloadFile from "_shared/_utilities/download-file";
import AssistantCitation from "ai-assistant/citations/assistant-citation";
import { HttpStatusCode } from "axios";
import { documentsApi } from "documents/_api/document-api";
import { documentDownloadFailureMessage } from "documents/_api/document-download-failure";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import { useState } from "react";
import { Link as RouterLink } from "react-router";

/** A server-selected app route must never turn into an external or executable URL. */
export function citationSourcePath(path: string | undefined): string | undefined {
  if (!path || path.length > 1024) return undefined;
  try {
    const decoded = decodeURIComponent(path);
    return decoded.startsWith("/") && !decoded.startsWith("//")
      && !/[\\:{}]/.test(decoded)
      && !Array.from(decoded).some(character => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127)
      ? path : undefined;
  } catch {
    return undefined;
  }
}

export const DELETED_DOCUMENT_MESSAGE = "This document has been deleted.";

type DownloadOutcome = "saved" | "deleted" | "failed";

/**
 * Fetches and saves one cited document, and says how that went.
 *
 * Dispatched without a subscription, so nothing holds the cache entry once it settles and the
 * endpoint's `keepUnusedDataFor: 0` drops the file's bytes as soon as they are saved (#5992). The
 * lazy hook kept the last download in the cache for as long as the citation panel was mounted.
 * `forceRefetch` keeps every click a fresh, permission-checked request.
 *
 * At module level because a `try` holding conditionals makes the React compiler skip the whole
 * component.
 */
async function downloadCitation(dispatch: AppDispatch, documentId: string): Promise<DownloadOutcome> {
  try {
    const response = await dispatch(documentsApi.endpoints.downloadDocument.initiate(
      { documentId },
      { subscribe: false, forceRefetch: true },
    )).unwrap();
    await downloadFile(response);
    return "saved";
  } catch (error) {
    // A 404 from the download endpoint means the document is gone (#5983). It answers a deletion
    // with a bare NotFound, which `documentDownloadFailureMessage` cannot read, so it used to say
    // "Failed to download file" and invite a retry that can never work. A refusal (403) carries
    // its reason in a problem body and keeps that path.
    if (isFetchBaseQueryError(error) && error.status === HttpStatusCode.NotFound) {
      enqueueSnackbar(DELETED_DOCUMENT_MESSAGE, { variant: "error" });
      return "deleted";
    }
    enqueueSnackbar(documentDownloadFailureMessage(error, "Failed to download file"), { variant: "error" });
    return "failed";
  }
}

function downloadText(downloading: boolean, deleted: boolean): string {
  if (deleted) return "Document deleted";
  return downloading ? "Downloading…" : "Download file";
}

function CitationDownload({ citation }: Readonly<{ citation: AssistantCitation }>) {
  const dispatch = useAttorneyHubDispatch();
  const [downloading, setDownloading] = useState(false);
  const [deleted, setDeleted] = useState(false);

  const handleDownload = async () => {
    if (downloading || deleted || !citation.downloadDocumentId || citation.downloadDocumentId.isEmpty) return;
    setDownloading(true);
    // The same authenticated endpoint as the Documents UI, with a fresh permission check.
    const outcome = await downloadCitation(dispatch, citation.downloadDocumentId.value);
    if (outcome === "deleted") setDeleted(true);
    setDownloading(false);
  };

  const text = downloadText(downloading, deleted);
  // The accessible name starts with the visible text (WCAG 2.5.3), so "click Download file" works
  // for a voice-control user, and the source's name follows to tell two panels' links apart.
  return (
    <Link component="button" type="button" onClick={handleDownload} disabled={downloading || deleted}
      aria-label={`${text}: ${citation.sourceLabel}`} aria-busy={downloading} sx={{ mt: 1 }}>
      {text}
    </Link>
  );
}

export default function CitationSourceLink({ citation }: Readonly<{ citation: AssistantCitation }>) {
  if (citation.withheld) return null;
  if (citation.downloadDocumentId && !citation.downloadDocumentId.isEmpty) {
    return <CitationDownload citation={citation} />;
  }
  // The destination, not the bare path: a knowledge base section opens at its heading (#6019).
  const path = citationSourcePath(citation.sourceDestination);
  return path ? (
    <Link component={RouterLink} to={path} aria-label={`Open source: ${citation.sourceLabel}`} sx={{ mt: 1, display: "inline-block" }}>
      Open source
    </Link>
  ) : null;
}
